import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Trash2, Clock, Mail, Send, Check, Briefcase, Building2, Hash, Calendar, FileText, Flag, Package } from 'lucide-react';
import { Modal, ModalBody, ModalFooter, Button, Toggle, Input, ConfirmDialog } from './ui';
import { ProjectService } from '../services/projectService';
import { HoldEmailService } from '../services/holdEmailService';
import { formatDate } from '../utils/dateFormatting';
import MultiSelectSearchable from './MultiSelectSearchable';
import { supabase } from '../lib/supabase';
import { formatJobRevNumber } from '../utils/projectFormatting';
import './ModelBuildingManagerModal.css';

const ModelBuildingManagerModal = ({ project, clients, contacts, discrepancies, isOpen, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    jobNumber: '',
    rev: '',
    contactId: '',
    clientId: '',
    modelingStatus: '',
    modelingState: '',
    initialAcceptance: '',
    approximateAcreage: '',
    additionalInfo: '',
    additionalRequests: [],
    holdStartDate: '',
    holdInitialFollowUpDays: '',
    holdReason: '',
    holdNotes: '',
    holdReminderSentDates: []
  });
  
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  
  // Email tracking state
  const [emailTracking, setEmailTracking] = useState(null);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendOneTimeEmail, setSendOneTimeEmail] = useState(false);  // New state for one-time email
  const [autoRecurringEmail, setAutoRecurringEmail] = useState(false);
  
  // Separate state for one-time and recurring emails
  const [oneTimeEmailPreview, setOneTimeEmailPreview] = useState('');
  const [oneTimeEmailSubject, setOneTimeEmailSubject] = useState('');
  const [oneTimeEmailHtml, setOneTimeEmailHtml] = useState('');
  
  const [recurringEmailPreview, setRecurringEmailPreview] = useState('');
  const [recurringEmailSubject, setRecurringEmailSubject] = useState('');
  const [recurringEmailHtml, setRecurringEmailHtml] = useState('');
  
  // Shared email preview state (for current active editor)
  const [emailPreview, setEmailPreview] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  
  // State to track which email type is being edited
  const [editingEmailType, setEditingEmailType] = useState('one-time'); // 'one-time' or 'recurring'
  
  const [emailTo, setEmailTo] = useState([]);
  const [emailCc, setEmailCc] = useState([]);
  const [initialFollowUpDays, setInitialFollowUpDays] = useState(7);
  const [recurringFollowUpDays, setRecurringFollowUpDays] = useState(7);
  // const [customMessage, setCustomMessage] = useState(''); // Column doesn't exist in DB
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailSendError, setEmailSendError] = useState(null);
  const [updatingPreview, setUpdatingPreview] = useState(false);
  
  // Discrepancy-related state
  const [includeDiscrepancies, setIncludeDiscrepancies] = useState(false);
  const [projectDiscrepancies, setProjectDiscrepancies] = useState([]);
  const [selectedDiscrepancyIds, setSelectedDiscrepancyIds] = useState([]);
  const [loadingDiscrepancies, setLoadingDiscrepancies] = useState(false);

  // Load discrepancies for the project
  const loadDiscrepancies = useCallback(async (projectId) => {
    setLoadingDiscrepancies(true);
    try {
      const { data, error } = await supabase
        .from('discrepancies')
        .select('id, type, status, created_at, additional_information, sent_date, markup_attachment, loom_link, file_share_link, created_by_name')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading discrepancies:', error);
      } else {
        setProjectDiscrepancies(data || []);
        // Initially select all discrepancies when loaded
        if (includeDiscrepancies && data && data.length > 0) {
          setSelectedDiscrepancyIds(data.map(d => d.id));
        }
      }
    } catch (error) {
      console.error('Error fetching discrepancies:', error);
    } finally {
      setLoadingDiscrepancies(false);
    }
  }, [includeDiscrepancies]);

  // Load email tracking data
  const loadEmailTracking = useCallback(async (projectId) => {
    try {
      const tracking = await HoldEmailService.getTracking(projectId);
      if (tracking) {
        setEmailTracking(tracking);
        
        // Load one-time email data if it exists
        if (tracking.email_preview || tracking.email_html || tracking.email_subject) {
          setOneTimeEmailPreview(tracking.email_preview || '');
          setOneTimeEmailSubject(tracking.email_subject || '');
          setOneTimeEmailHtml(tracking.email_html || '');
        }
        
        // Load recurring email data if it exists
        if (tracking.recurring_email_preview || tracking.recurring_email_html || tracking.recurring_email_subject) {
          setRecurringEmailPreview(tracking.recurring_email_preview || '');
          setRecurringEmailSubject(tracking.recurring_email_subject || '');
          setRecurringEmailHtml(tracking.recurring_email_html || '');
        }
        
        // Set the active editor to whichever has content (prefer recurring if both exist)
        if (tracking.recurring_email_preview || tracking.recurring_email_html || tracking.recurring_email_subject) {
          setEmailPreview(tracking.recurring_email_preview || '');
          setEmailSubject(tracking.recurring_email_subject || '');
          setEmailHtml(tracking.recurring_email_html || '');
        } else if (tracking.email_preview || tracking.email_html || tracking.email_subject) {
          setEmailPreview(tracking.email_preview || '');
          setEmailSubject(tracking.email_subject || '');
          setEmailHtml(tracking.email_html || '');
        }
        setAutoRecurringEmail(tracking.auto_recurring_email || false);
        setIncludeDiscrepancies(tracking.include_discrepancies || false);
        
        // Load selected discrepancy IDs from saved data
        if (tracking.discrepancies_included && Array.isArray(tracking.discrepancies_included)) {
          const savedDiscrepancyIds = tracking.discrepancies_included.map(d => d.id).filter(Boolean);
          setSelectedDiscrepancyIds(savedDiscrepancyIds);
        }
        
        // Convert email addresses back to contact IDs for the UI
        const toValues = tracking.email_to || [];
        const ccValues = tracking.email_cc || [];
        
        const toIds = toValues.map(value => {
          if (typeof value === 'number') {
            return value;
          } else {
            const contact = contacts.find(c => c.email === value);
            return contact ? contact.id : null;
          }
        }).filter(Boolean);
        
        const ccIds = ccValues.map(value => {
          if (typeof value === 'number') {
            return value;
          } else {
            const contact = contacts.find(c => c.email === value);
            return contact ? contact.id : null;
          }
        }).filter(Boolean);
        
        // Only update To/Cc if we have values from the database
        // Don't clear existing values that might have been set by the toggle handler
        if (toIds.length > 0) {
          setEmailTo(toIds);
        } else if (emailTo.length === 0 && project?.contactId) {
          // No saved recipients and To field is empty - set default
          const projectContact = contacts.find(c => c.id === project.contactId);
          if (projectContact && projectContact.email) {
            setEmailTo([project.contactId]);
          }
        }
        if (ccIds.length > 0) {
          setEmailCc(ccIds);
        }
        setInitialFollowUpDays(tracking.initial_follow_up_days || 7);
        setRecurringFollowUpDays(tracking.recurring_follow_up_days || 7);
        // setCustomMessage(tracking.custom_message || ''); // Column doesn't exist
      } else {
        // No existing tracking - set default To recipient as project contact if To is empty
        if (project?.contactId && emailTo.length === 0) {
          // Verify the contact exists and has an email
          const projectContact = contacts.find(c => c.id === project.contactId);
          if (projectContact && projectContact.email) {
            setEmailTo([project.contactId]);
          }
        }
      }
    } catch (error) {
      console.error('Error loading email tracking:', error);
      // On error, still try to set default To recipient
      if (project?.contactId) {
        const projectContact = contacts.find(c => c.id === project.contactId);
        if (projectContact && projectContact.email) {
          setEmailTo([project.contactId]);
        }
      }
    }
  }, [contacts, project, emailTo.length]);

  // Load current user
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: userData } = await supabase
            .from('employees')
            .select('*')
            .eq('email', user.email)
            .single();
          
          if (userData) {
            setCurrentUser(userData);
          }
        }
      } catch (error) {
        console.error('Error loading current user:', error);
      }
    };
    
    if (isOpen) {
      loadCurrentUser();
      // Clear any previous errors when modal opens
      setError(null);
      // Reset generating state in case it was stuck
      setGeneratingEmail(false);
    }
  }, [isOpen]);

  // Generate email for one-time send (simpler, direct email)
  const handleGenerateOneTimeEmail = useCallback(async () => {
    if (!project || !sendOneTimeEmail) return;
    
    console.log('Generating one-time email...');
    setGeneratingEmail(true);
    setError(null);
    
    try {
      // Generate a simple email for one-time send
      let emailContent = '';
      const projectName = project?.projectName || project?.name || 'Project';
      let subject = `${projectName} - Status Update`;
      
      // Get contact name for greeting
      const primaryContactId = emailTo[0] || project?.contactId;
      let firstName = '';
      if (primaryContactId) {
        const contact = contacts.find(c => c.id === primaryContactId);
        if (contact && contact.name) {
          firstName = contact.name.split(' ')[0];
          emailContent = `${firstName},\n\n`;
        }
      }
      
      // Check for quick deadline situations
      const now = new Date();
      const finishGradeDueDate = project?.finishGradeDueDate || project?.finish_grade_due_date;
      const initialAcceptance = project?.initialAcceptance || project?.initial_acceptance;
      const projectedCompletionDate = project?.finalProjectedCompletionDate || project?.final_projected_completion_date || 
                                      project?.projectedCompletionDate || project?.projected_completion_date;
      
      let isQuickDeadline = false;

      // Check if finish grade is due soon (within 7 days) or if time from acceptance to due is less than 14 days
      if (finishGradeDueDate) {
        const finishGradeDue = new Date(finishGradeDueDate);
        const daysUntilDue = Math.floor((finishGradeDue - now) / (1000 * 60 * 60 * 24));
        
        // Check if initial acceptance to finish grade due is less than 14 days
        let totalProjectDays = null;
        if (initialAcceptance) {
          const acceptanceDate = new Date(initialAcceptance);
          totalProjectDays = Math.floor((finishGradeDue - acceptanceDate) / (1000 * 60 * 60 * 24));
        }
        
        // Quick deadline if: due within 7 days OR total project time is less than 14 days
        if ((daysUntilDue >= 0 && daysUntilDue < 7) || (totalProjectDays !== null && totalProjectDays < 14)) {
          isQuickDeadline = true;
          const formattedDueDate = finishGradeDue.toLocaleDateString('en-US', { 
            weekday: 'long',
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          });
          
          emailContent += `We've received your project ${projectName} with a finish grade due date of ${formattedDueDate}.\n\n`;
          
          if (totalProjectDays !== null && totalProjectDays < 14) {
            emailContent += `We noticed this deadline is ${totalProjectDays} days from submission, which is less than our standard 2-week timeframe.\n\n`;
          } else {
            emailContent += `We noticed this deadline is approaching quickly with only ${daysUntilDue} days remaining.\n\n`;
          }
          
          emailContent += `Could you please confirm this is the correct due date? Please note that projects submitted with less than 2 weeks notice may not be completed by the requested date due to our current workload.\n\n`;
          emailContent += `If you need to adjust the deadline or have any questions, please let us know as soon as possible.\n\n`;
          emailContent += `Thank you for your understanding.`;
          
          subject = `${projectName} - Due Date Confirmation Required`;
        }
      }
      
      // Check if projected completion is after due date
      if (!isQuickDeadline && projectedCompletionDate && finishGradeDueDate) {
        const projectedCompletion = new Date(projectedCompletionDate);
        const dueDate = new Date(finishGradeDueDate);
        
        if (projectedCompletion > dueDate) {
          isQuickDeadline = true;
          const daysLate = Math.floor((projectedCompletion - dueDate) / (1000 * 60 * 60 * 24));
          const formattedDueDate = dueDate.toLocaleDateString('en-US', { 
            weekday: 'long',
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          });
          const formattedProjectedDate = projectedCompletion.toLocaleDateString('en-US', { 
            weekday: 'long',
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          });
          
          emailContent += `We're writing regarding your project ${projectName} with a due date of ${formattedDueDate}.\n\n`;
          emailContent += `We project your model will be completed by ${formattedProjectedDate}, which is ${daysLate} days after your requested date.\n\n`;
          emailContent += `Please let us know if the original due date is flexible or if you'd like to discuss options to expedite the project.\n\n`;
          emailContent += `We apologize for any inconvenience and appreciate your understanding.`;
          
          subject = `${projectName} - Schedule Update Required`;
        }
      }
      
      // If not a quick deadline, generate standard status email
      if (!isQuickDeadline) {
        if (project.modelingState === 'Hold') {
          emailContent += `Project ${projectName} is currently on hold.\n\n`;
          if (project.holdReason) {
            const holdReasonText = project.holdReason
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase());
            emailContent += `Hold Reason: ${holdReasonText}\n`;
          }
          if (project.holdNotes) {
            emailContent += `Notes: ${project.holdNotes}\n`;
          }
        } else if (project?.dueDate && new Date(project.dueDate) < new Date()) {
          const daysOverdue = Math.ceil((new Date() - new Date(project.dueDate)) / (1000 * 60 * 60 * 24));
          emailContent += `Project ${projectName} is ${daysOverdue} days overdue.\n\n`;
        } else {
          emailContent += `This is a status update for project ${projectName}.\n\n`;
        }
        
        // Custom message feature removed - column doesn't exist in DB
        
        // Only add discrepancies if the switch is explicitly turned on
        if (includeDiscrepancies && selectedDiscrepancyIds.length > 0) {
          emailContent += '\n\nProject Discrepancies:\n';
          const selectedDiscs = projectDiscrepancies.filter(d => selectedDiscrepancyIds.includes(d.id));
          selectedDiscs.forEach((disc) => {
            emailContent += `\n${disc.type}:\n`;
            if (disc.additional_information) {
              emailContent += `${disc.additional_information}\n`;
            }
            if (disc.markup_attachment && disc.markup_attachment.length > 0) {
              disc.markup_attachment.forEach((file) => {
                if (file.url) {
                  emailContent += `File link: ${file.url}\n`;
                }
              });
            }
            if (disc.loom_link) {
              emailContent += `Video: ${disc.loom_link}\n`;
            }
          });
        }
      }
      
      // Set email subject and content
      setEmailSubject(subject);
      setEmailPreview(emailContent);
      
      // Convert to HTML (reuse existing HTML conversion)
      // The HTML conversion will happen via the existing useEffect
      
    } catch (error) {
      console.error('Error generating one-time email:', error);
      setError('Failed to generate email: ' + error.message);
    } finally {
      setGeneratingEmail(false);
    }
  }, [project, sendOneTimeEmail, emailTo, contacts, includeDiscrepancies, selectedDiscrepancyIds, projectDiscrepancies]);

  // Generate email preview for recurring notifications
  const handleGenerateEmail = useCallback(async () => {
    // Prevent multiple simultaneous generations
    if (generatingEmail) {
      console.log('Already generating email, skipping...');
      return;
    }
    
    if (!project) {
      console.error('No project available for email generation');
      setError('No project selected');
      return;
    }
    
    console.log('Starting email generation...');
    setGeneratingEmail(true);
    setError(null); // Clear any previous errors
    
    try {
      const trackingData = {
        initial_follow_up_days: initialFollowUpDays,
        recurring_follow_up_days: recurringFollowUpDays,
        auto_recurring_email: autoRecurringEmail,
        last_email_sent_date: emailTracking?.last_email_sent_date
      };
      
      console.log('Generating email with tracking data:', trackingData);
      
      // Filter discrepancies to only selected ones
      const selectedDiscrepancies = includeDiscrepancies && selectedDiscrepancyIds.length > 0
        ? projectDiscrepancies.filter(d => selectedDiscrepancyIds.includes(d.id))
        : null;
      
      const preview = HoldEmailService.generateEmailPreview(
        project, 
        trackingData, 
        contacts, 
        currentUser,
        selectedDiscrepancies
      );
      
      if (!preview || !preview.body) {
        throw new Error('Failed to generate email preview content');
      }
      
      setEmailSubject(preview.subject);
      setEmailPreview(preview.body);
      
      console.log('Email preview generated successfully');
      
      // Generate HTML version
      const htmlContent = await HoldEmailService.generateHTMLEmail(
        preview.body,
        project,
        currentUser,
        contacts,
        selectedDiscrepancies
      );
      setEmailHtml(htmlContent);
      
      console.log('HTML email generated successfully');
    } catch (error) {
      console.error('Error generating email preview:', error);
      setError(error.message || 'Failed to generate email preview');
      // Reset states on error
      setEmailPreview('');
      setEmailSubject('');
      setEmailHtml('');
    } finally {
      // Always reset generating state
      setTimeout(() => {
        setGeneratingEmail(false);
      }, 100); // Small delay to prevent button bounce
    }
  }, [project, initialFollowUpDays, recurringFollowUpDays, autoRecurringEmail, emailTracking, contacts, currentUser, includeDiscrepancies, projectDiscrepancies, selectedDiscrepancyIds, generatingEmail]);

  // Update HTML when email preview changes - reduced debounce for more responsive feel
  useEffect(() => {
    // Show updating indicator immediately
    setUpdatingPreview(true);
    
    const updateHtml = async () => {
      if (emailPreview) {
        try {
          // Check current editing type to determine which HTML format to use
          if (editingEmailType === 'one-time') {
            // For one-time emails, create plain HTML without colored boxes
            const plainHtml = `
              <!DOCTYPE html>
              <html>
              <head>
                <style>
                  body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    line-height: 1.6;
                    color: #333;
                    max-width: 600px;
                    margin: 0 auto;
                    padding: 20px;
                  }
                  p {
                    margin: 0 0 1em 0;
                  }
                </style>
              </head>
              <body>
                ${emailPreview.split('\n\n').map(paragraph => 
                  `<p>${paragraph.replace(/\n/g, '<br>')}</p>`
                ).join('')}
              </body>
              </html>
            `;
            setEmailHtml(plainHtml);
          } else {
            // For recurring emails, use the existing HTML generation with colored boxes
            // Filter discrepancies to only selected ones
            const selectedDiscrepancies = includeDiscrepancies && selectedDiscrepancyIds.length > 0
              ? projectDiscrepancies.filter(d => selectedDiscrepancyIds.includes(d.id))
              : null;
            
            const htmlContent = await HoldEmailService.generateHTMLEmail(
              emailPreview,
              project,
              currentUser,
              contacts,
              selectedDiscrepancies
            );
            setEmailHtml(htmlContent);
          }
        } catch (error) {
          console.error('Error updating HTML:', error);
        }
      } else {
        // Clear the preview when email content is empty
        setEmailHtml('<div style="padding: 20px; text-align: center; color: #666;">Email preview will appear here</div>');
      }
      setUpdatingPreview(false);
    };
    
    // Reduced from 500ms to 150ms for more responsive updates
    const timer = setTimeout(updateHtml, 150);
    return () => clearTimeout(timer);
  }, [emailPreview, project, currentUser, contacts, includeDiscrepancies, selectedDiscrepancyIds, projectDiscrepancies, editingEmailType]);

  // Automatically generate email when relevant fields change
  useEffect(() => {
    // Generate appropriate email based on which mode is enabled
    if (!sendEmail && !sendOneTimeEmail) return;
    
    // Set up debounced auto-generation
    const timer = setTimeout(() => {
      // Regenerate email whenever dependencies change (unless already generating)
      if (!generatingEmail) {
        if (sendOneTimeEmail) {
          // For one-time emails, generate a simpler email
          handleGenerateOneTimeEmail();
        } else if (sendEmail) {
          // For recurring emails, use the existing template-based generation
          handleGenerateEmail();
        }
      }
    }, 500); // 500ms debounce to avoid excessive regeneration
    
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sendEmail,
    sendOneTimeEmail,
    selectedDiscrepancyIds,
    includeDiscrepancies,
    initialFollowUpDays,
    recurringFollowUpDays,
    // customMessage, // Removed - column doesn't exist
    autoRecurringEmail
  ]);

  useEffect(() => {
    if (project) {
      console.log('Modal: Full project data received:', project);
      
      setFormData({
        jobNumber: project.jobNumber || '',
        rev: project.rev || '',
        contactId: project.contactId || '',
        clientId: project.clientId || '',
        modelingStatus: project.modelingStatus || '',
        modelingState: project.modelingState || '',
        initialAcceptance: project.initialAcceptance ? formatDate(project.initialAcceptance, 'input') : '',
        approximateAcreage: project.approximateAcreage || '',
        additionalInfo: project.modelRequestAdditionalInfo || '',
        additionalRequests: project.additionalRequests || [],
        holdStartDate: project.holdStartDate ? (typeof project.holdStartDate === 'string' ? project.holdStartDate : '') : '',
        holdInitialFollowUpDays: project.holdInitialFollowUpDays !== null && project.holdInitialFollowUpDays !== undefined ? project.holdInitialFollowUpDays : '',
        holdReason: project.holdReason || '',
        holdNotes: project.holdNotes || '',
        holdReminderSentDates: project.holdReminderSentDates || []
      });
      
      // Load email tracking if project is on hold OR overdue OR email features are enabled
      const isOverdue = project.dueDate && new Date(project.dueDate) < new Date();
      if (project.modelingState === 'Hold' || isOverdue) {
        loadEmailTracking(project.id);
        loadDiscrepancies(project.id);
      } else if (project?.contactId && (sendOneTimeEmail || sendEmail)) {
        // Even if not on hold, set default contact when email features are enabled
        const projectContact = contacts.find(c => c.id === project.contactId);
        if (projectContact && projectContact.email) {
          setEmailTo([project.contactId]);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, loadEmailTracking, loadDiscrepancies, sendOneTimeEmail, contacts, sendEmail]);


  // Handle one-time email toggle
  const handleSendOneTimeEmailToggle = (checked) => {
    setSendOneTimeEmail(checked);
    
    if (checked) {
      // Auto-fill To field with project contact when enabling one-time email
      if (project?.contactId && emailTo.length === 0) {
        const projectContact = contacts.find(c => c.id === project.contactId);
        if (projectContact && projectContact.email) {
          setEmailTo([project.contactId]);
        }
      }
      
      // Auto-generate one-time email content IF we're currently viewing one-time
      if (editingEmailType === 'one-time') {
        setTimeout(() => {
          if (project && contacts) {
            handleGenerateOneTimeEmail();
          }
        }, 100);
      }
    }
  };

  // Handle email type toggle (switch between one-time and recurring)
  const handleEmailTypeToggle = () => {
    const newType = editingEmailType === 'one-time' ? 'recurring' : 'one-time';
    console.log('Toggling email type from', editingEmailType, 'to', newType);
    
    // Save current email state before switching
    if (editingEmailType === 'one-time') {
      // Save one-time email state
      console.log('Saving one-time email state:', { preview: emailPreview?.substring(0, 50) });
      setOneTimeEmailPreview(emailPreview);
      setOneTimeEmailSubject(emailSubject);
      setOneTimeEmailHtml(emailHtml);
      
      // Load recurring email state
      console.log('Loading recurring email state:', { preview: recurringEmailPreview?.substring(0, 50) });
      setEmailPreview(recurringEmailPreview);
      setEmailSubject(recurringEmailSubject);
      setEmailHtml(recurringEmailHtml);
      
      // Generate recurring email if it's empty
      if (!recurringEmailPreview && sendEmail) {
        console.log('Generating recurring email because it is empty');
        setTimeout(() => {
          if (project && contacts && currentUser) {
            handleGenerateEmail();
          }
        }, 100);
      }
    } else {
      // Save recurring email state
      console.log('Saving recurring email state:', { preview: emailPreview?.substring(0, 50) });
      setRecurringEmailPreview(emailPreview);
      setRecurringEmailSubject(emailSubject);
      setRecurringEmailHtml(emailHtml);
      
      // Load one-time email state
      console.log('Loading one-time email state:', { preview: oneTimeEmailPreview?.substring(0, 50) });
      setEmailPreview(oneTimeEmailPreview);
      setEmailSubject(oneTimeEmailSubject);
      setEmailHtml(oneTimeEmailHtml);
      
      // Generate one-time email if it's empty
      if (!oneTimeEmailPreview && sendOneTimeEmail) {
        console.log('Generating one-time email because it is empty');
        setTimeout(() => {
          if (project && contacts) {
            handleGenerateOneTimeEmail();
          }
        }, 100);
      }
    }
    
    setEditingEmailType(newType);
  };
  
  // Handle recurring email toggle
  const handleSendEmailToggle = (checked) => {
    setSendEmail(checked);
    
    if (checked) {
      // Auto-fill To field with project contact when enabling recurring email
      if (project?.contactId && emailTo.length === 0) {
        const projectContact = contacts.find(c => c.id === project.contactId);
        if (projectContact && projectContact.email) {
          setEmailTo([project.contactId]);
        }
      }

      // Auto-generate recurring email content IF we're currently viewing recurring
      if (editingEmailType === 'recurring') {
        setTimeout(() => {
          if (project && emailTracking && contacts && currentUser) {
            handleGenerateEmail();
          }
        }, 100);
      }
    }
  };

  // Handle auto recurring email toggle with immediate save
  const handleAutoRecurringToggle = async (checked) => {
    setAutoRecurringEmail(checked);
    
    // If turning off auto recurring, also turn off edit recurring notification
    if (!checked) {
      setSendEmail(false);
    }
    
    // Save immediately to database if project is on hold or overdue
    const isOverdue = project?.dueDate && new Date(project.dueDate) < new Date();
    if (project && (project.modelingState === 'Hold' || isOverdue)) {
      try {
        console.log('💾 Saving auto recurring email setting:', checked);
        
        // Just update the auto_recurring_email field
        const updateData = {
          auto_recurring_email: checked
          // Don't set email_status - let it keep its current value
        };
        
        await HoldEmailService.upsertTracking(project.id, updateData);
        console.log('✅ Auto recurring email setting saved');
      } catch (error) {
        console.error('❌ Error saving auto recurring email:', error);
        setError('Failed to save auto recurring email setting');
      }
    }
  };

  // Handle sending email
  const handleSendEmail = async () => {
    if (!project || !emailPreview || emailTo.length === 0) {
      setError('Cannot send email: missing required information');
      return;
    }

    setSendingEmail(true);
    setEmailSendError(null);
    setEmailSent(false);
    
    try {
      // First save the current email template
      await handleSave(false);
      
      // Convert contact IDs to email addresses
      const emailToAddresses = emailTo.map(contactId => {
        const contact = contacts.find(c => c.id === contactId);
        return contact ? contact.email?.toLowerCase() : null;
      }).filter(Boolean);
      
      const emailCcAddresses = emailCc.map(contactId => {
        const contact = contacts.find(c => c.id === contactId);
        return contact ? contact.email?.toLowerCase() : null;
      }).filter(Boolean);
      
      // Prepare additional data based on email type
      let additionalData = {
        include_discrepancies: includeDiscrepancies,
        discrepancies_included: includeDiscrepancies && selectedDiscrepancyIds.length > 0 
          ? projectDiscrepancies.filter(d => selectedDiscrepancyIds.includes(d.id))
          : []
      };
      
      if (sendOneTimeEmail) {
        // For one-time emails, minimal additional data
        additionalData.is_one_time_email = true;
      } else if (sendEmail) {
        // For recurring emails, include follow-up settings
        additionalData.initial_follow_up_days = initialFollowUpDays;
        additionalData.recurring_follow_up_days = recurringFollowUpDays;
        additionalData.auto_recurring_email = autoRecurringEmail;
      }
      
      // Send the email using HoldEmailService
      const result = await HoldEmailService.sendHoldEmail(
        project.id,
        emailPreview,
        emailHtml || emailPreview,
        emailSubject,
        emailToAddresses,
        emailCcAddresses,
        additionalData
      );
      
      console.log('✅ Email sent successfully:', result);
      setEmailSent(true);
      
      // Show success message for 3 seconds
      setTimeout(() => {
        setEmailSent(false);
      }, 3000);
      
      // Refresh the email tracking data
      await loadEmailTracking(project.id);
      
    } catch (error) {
      console.error('❌ Error sending email:', error);
      setEmailSendError(error.message || 'Failed to send email');
      // Clear error after 5 seconds
      setTimeout(() => {
        setEmailSendError(null);
      }, 5000);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSave = async (shouldSendEmail = false) => {
    try {
      setSaving(true);
      setError(null);

      const fieldsToUpdate = {
        // Only update fields that should be editable
        model_request_additional_info: formData.additionalInfo || null
      };

      console.log('📝 Fields to update:', fieldsToUpdate);
      
      // Save email tracking data if project is on hold OR overdue
      const isOverdue = project?.dueDate && new Date(project.dueDate) < new Date();
      if (project.modelingState === 'Hold' || isOverdue) {
        console.log('🔍 Hold email conditions check:', {
          modelingState: project.modelingState,
          shouldSendEmail,
          emailToLength: emailTo.length,
          hasEmailPreview: !!emailPreview,
          emailSubject,
          sendEmail,
          sendOneTimeEmail
        });

        // Save tracking data to the database
        // Convert contact IDs to email addresses for database storage
        const emailToAddresses = emailTo.map(contactId => {
          const contact = contacts.find(c => c.id === contactId);
          return contact ? contact.email?.toLowerCase() : null;
        }).filter(Boolean);
        
        const emailCcAddresses = emailCc.map(contactId => {
          const contact = contacts.find(c => c.id === contactId);
          return contact ? contact.email?.toLowerCase() : null;
        }).filter(Boolean);
        
        // Build email tracking data with both one-time and recurring email info
        let emailTrackingData = {
          send_email: false,
          auto_recurring_email: autoRecurringEmail,
          email_to: emailToAddresses,
          email_cc: emailCcAddresses,
          email_status: 'draft',
          hold_status: 'active',
          include_discrepancies: includeDiscrepancies,
          discrepancies_included: includeDiscrepancies && selectedDiscrepancyIds.length > 0 
            ? projectDiscrepancies.filter(d => selectedDiscrepancyIds.includes(d.id))
            : null
        };
        
        // Save the current email content to the appropriate state before saving
        if (editingEmailType === 'one-time') {
          // Currently editing one-time, so save current content as one-time
          if (sendOneTimeEmail && emailPreview) {
            emailTrackingData.email_preview = emailPreview;
            emailTrackingData.email_html = emailHtml || emailPreview;
            emailTrackingData.email_subject = emailSubject;
          }
          // Also save recurring if it exists in state
          if (sendEmail && recurringEmailPreview) {
            emailTrackingData.recurring_email_preview = recurringEmailPreview;
            emailTrackingData.recurring_email_html = recurringEmailHtml || recurringEmailPreview;
            emailTrackingData.recurring_email_subject = recurringEmailSubject;
            emailTrackingData.initial_follow_up_days = initialFollowUpDays;
            emailTrackingData.recurring_follow_up_days = recurringFollowUpDays;
          }
        } else {
          // Currently editing recurring, so save current content as recurring
          if (sendEmail && emailPreview) {
            emailTrackingData.recurring_email_preview = emailPreview;
            emailTrackingData.recurring_email_html = emailHtml || emailPreview;
            emailTrackingData.recurring_email_subject = emailSubject;
            emailTrackingData.initial_follow_up_days = initialFollowUpDays;
            emailTrackingData.recurring_follow_up_days = recurringFollowUpDays;
          }
          // Also save one-time if it exists in state
          if (sendOneTimeEmail && oneTimeEmailPreview) {
            emailTrackingData.email_preview = oneTimeEmailPreview;
            emailTrackingData.email_html = oneTimeEmailHtml || oneTimeEmailPreview;
            emailTrackingData.email_subject = oneTimeEmailSubject;
          }
        }
        
        console.log('📧 Saving email tracking with include_discrepancies:', includeDiscrepancies);
        await HoldEmailService.upsertTracking(project.id, emailTrackingData);
        console.log('✅ Email tracking saved successfully');
        
        // Send email using the new sendHoldEmail method
        if (shouldSendEmail) {
          console.log('📮 Checking email send conditions...');
          
          if (!emailTo || emailTo.length === 0) {
            console.error('❌ Cannot send email: No recipients selected');
            throw new Error('Please select at least one recipient');
          }
          
          if (!emailPreview) {
            console.error('❌ Cannot send email: No email content');
            throw new Error('Please generate email content first');
          }
          
          console.log('📤 All conditions met, sending hold email...');
          
          try {
            setSendingEmail(true);
            
            // Convert contact IDs to email addresses for sending
            const emailToAddresses = emailTo.map(contactId => {
              const contact = contacts.find(c => c.id === contactId);
              return contact ? contact.email?.toLowerCase() : contactId;
            }).filter(Boolean);
            
            const emailCcAddresses = emailCc.map(contactId => {
              const contact = contacts.find(c => c.id === contactId);
              return contact ? contact.email?.toLowerCase() : contactId;
            }).filter(Boolean);
            
            console.log('📧 Final email addresses:', {
              to: emailToAddresses,
              cc: emailCcAddresses
            });
            
            // Use the new sendHoldEmail method from HoldEmailService
            const result = await HoldEmailService.sendHoldEmail(
              project.id,
              emailPreview,
              emailHtml || emailPreview,
              emailSubject,
              emailToAddresses,
              emailCcAddresses,
              {
                auto_recurring_email: autoRecurringEmail,
                initial_follow_up_days: initialFollowUpDays,
                recurring_follow_up_days: recurringFollowUpDays,
                is_one_time_email: sendOneTimeEmail // Pass flag to indicate this is a one-time email
              }
            );
            console.log('✅ Email sent successfully:', result);
            
            // Try to update tracking with sent date (non-blocking)
            // Skip this for one-time emails - let N8N workflow handle status change
            if (!sendOneTimeEmail) {
              try {
                await HoldEmailService.markEmailSentByProjectId(project.id);
                console.log('✅ Tracking updated with sent date');
              } catch (markError) {
                console.error('⚠️ Failed to mark email as sent in tracking:', markError);
              }
            } else {
              console.log('📧 One-time email - status remains "draft" for N8N workflow');
            }
          } catch (emailError) {
            console.error('❌ Failed to send email:', emailError);
            throw emailError;
          } finally {
            setSendingEmail(false);
          }
        }
      }

      if (onSave) {
        await onSave();
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
      setSendingEmail(false);
    }
  };

  const handleDelete = async () => {
    if (!project?.id) return;
    
    try {
      setDeleting(true);
      setError(null);
      
      await ProjectService.deleteProject(project.id);
      
      if (onDelete) {
        await onDelete();
      }
      
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error('Error deleting project:', err);
      setError(err.message || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  // Get all contacts with client information for the dropdown
  const allContactsWithClients = contacts.map(c => ({
    ...c,
    clientName: clients.find(client => client.id === c.clientId)?.name || 'Unknown'
  }));

  return (
    <Modal trackAs="ModelBuildingManagerModal"
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      showCloseButton={true}
      theme="dark"
    >
      {/* Standard Project Modal Header */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
          {/* Job Number and Project Name on one line - Standard for project modals */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
              <span
                style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-400)',
                  flexShrink: 0,
                }}
              >
                {formatJobRevNumber(project?.jobNumber, project?.rev)}
              </span>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {project?.projectName || project?.name || 'Project Details'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <ModalBody style={{ background: 'var(--dark-bg)', color: 'var(--gray-50)' }}>
          {error && (
            <div className="modal-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          
          {emailSendError && (
            <div className="modal-error" style={{ marginTop: 'var(--space-2)' }}>
              <AlertCircle size={16} />
              <span>{emailSendError}</span>
            </div>
          )}

          {emailSent && (
            <div style={{
              marginTop: 'var(--space-2)',
              padding: 'var(--space-3)',
              background: 'var(--success)',
              color: 'white',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              <Check size={16} />
              Email sent successfully!
            </div>
          )}


          {/* Project Details Section - Shows for ALL projects */}
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h2 style={{
              margin: 0,
              marginBottom: 'var(--space-4)',
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-200)',
            }}>
              Project Information
            </h2>

            <div className="project-info-grid">
              {/* Job Number */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}>
                <Hash size={18} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Job Number {formData.rev && parseInt(formData.rev) > 0 && `• Rev ${formData.rev}`}
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', lineHeight: '1.5' }}>
                    {formData.jobNumber || 'Not set'}
                  </div>
                </div>
              </div>

              {/* Client */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}>
                <Building2 size={18} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Client
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', lineHeight: '1.5' }}>
                    {clients.find(c => c.id === formData.clientId)?.name || 'Unknown'}
                  </div>
                </div>
              </div>

              {/* Modeling Status */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}>
                <Briefcase size={18} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Modeling Status
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', lineHeight: '1.5' }}>
                    {formData.modelingStatus || 'Not set'}
                    {formData.modelingStatus && (project?.lastStageChangeDate || project?.last_stage_change_date) && (
                      <span style={{ color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontStyle: 'italic', marginLeft: 'var(--space-2)' }}>
                        {(() => {
                          const statusChangeDate = new Date(project.lastStageChangeDate || project.last_stage_change_date);
                          const now = new Date();
                          statusChangeDate.setHours(0, 0, 0, 0);
                          now.setHours(0, 0, 0, 0);

                          const diffTime = now - statusChangeDate;
                          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                          if (diffDays === 0) {
                            return `(< 1 day)`;
                          } else if (diffDays === 1) {
                            return `(1 day)`;
                          } else {
                            return `(${diffDays} days)`;
                          }
                        })()}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Due Date */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}>
                <Calendar size={18} style={{
                  color: (() => {
                    const relevantDate = project?.dueDate || project?.due_date ||
                                     project?.finishGradeDueDate || project?.finish_grade_due_date;
                    if (!relevantDate) return 'var(--gray-400)';

                    const date = new Date(relevantDate);
                    const now = new Date();
                    date.setHours(0, 0, 0, 0);
                    now.setHours(0, 0, 0, 0);

                    return date < now ? 'var(--danger)' : 'var(--gray-400)';
                  })(),
                  marginTop: '2px',
                  flexShrink: 0
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Due Date
                  </label>
                  <div style={{
                    color: (() => {
                      const relevantDate = project?.dueDate || project?.due_date ||
                                       project?.finishGradeDueDate || project?.finish_grade_due_date;
                      if (!relevantDate) return 'var(--gray-100)';

                      const date = new Date(relevantDate);
                      const now = new Date();
                      date.setHours(0, 0, 0, 0);
                      now.setHours(0, 0, 0, 0);

                      return date < now ? 'var(--danger)' : 'var(--gray-100)';
                    })(),
                    fontSize: 'var(--text-sm)',
                    lineHeight: '1.5',
                    fontWeight: (() => {
                      const relevantDate = project?.dueDate || project?.due_date ||
                                       project?.finishGradeDueDate || project?.finish_grade_due_date;
                      if (!relevantDate) return 'normal';

                      const date = new Date(relevantDate);
                      const now = new Date();
                      date.setHours(0, 0, 0, 0);
                      now.setHours(0, 0, 0, 0);

                      return date < now ? 'var(--font-semibold)' : 'normal';
                    })()
                  }}>
                    {(() => {
                      const relevantDate = project?.dueDate || project?.due_date ||
                                     project?.finishGradeDueDate || project?.finish_grade_due_date;
                      if (!relevantDate) return 'Not set';

                      const date = new Date(relevantDate);
                      const now = new Date();
                      date.setHours(0, 0, 0, 0);
                      now.setHours(0, 0, 0, 0);

                      const diffTime = now - date;
                      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                      if (diffDays > 0) {
                        return `${date.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })} (${diffDays} day${diffDays !== 1 ? 's' : ''} overdue)`;
                      } else if (diffDays === 0) {
                        return `${date.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })} (Due today)`;
                      } else {
                        return date.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });
                      }
                    })()}
                  </div>
                </div>
              </div>

              {/* Approximate Acreage */}
              {formData.approximateAcreage && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--dark-surface)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-lg)',
                }}>
                  <FileText size={18} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-400)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Approximate Acreage
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', lineHeight: '1.5' }}>
                      {formData.approximateAcreage}
                    </div>
                  </div>
                </div>
              )}

              {/* High Priority */}
              {(project?.isHighPriority || project?.is_high_priority) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'rgba(167, 139, 250, 0.08)',
                  border: '1px solid rgba(167, 139, 250, 0.3)',
                  borderRadius: 'var(--radius-lg)',
                }}>
                  <Flag size={18} style={{ color: '#a78bfa', marginTop: '2px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: '#a78bfa',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Priority
                    </label>
                    <div style={{ color: '#c4b5fd', fontSize: 'var(--text-sm)', lineHeight: '1.5', fontWeight: 'var(--font-semibold)' }}>
                      High Priority
                    </div>
                  </div>
                </div>
              )}

              {/* Overdue Notice - Only show if overdue - Spans both columns */}
              {(() => {
                const relevantDate = project?.dueDate || project?.due_date ||
                                     project?.finishGradeDueDate || project?.finish_grade_due_date;
                if (!relevantDate) return false;

                const date = new Date(relevantDate);
                const now = new Date();
                date.setHours(0, 0, 0, 0);
                now.setHours(0, 0, 0, 0);

                return date < now;
              })() && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3)',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  gridColumn: '1 / -1',
                }}>
                  <AlertCircle size={16} style={{ color: 'var(--danger)', marginTop: '2px', flexShrink: 0 }} />
                  <p style={{ margin: 0, color: '#fca5a5', fontSize: 'var(--text-sm)', lineHeight: '1.5' }}>
                    This project is overdue. Consider placing it on hold and sending a notification to the client.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Over 24 Hr Discrepancies Section */}
          {discrepancies && (() => {
            const now = new Date();
            const twentyFourHoursAgo = new Date(now - 24 * 60 * 60 * 1000);
            
            const projectDiscrepancies = discrepancies.filter(d => d.project_id === project.id);
            const staleDiscrepancies = projectDiscrepancies.filter((disc) => {
              const createdAt = new Date(disc.created_at);
              const isStale = createdAt < twentyFourHoursAgo;
              const notSent = disc.status !== 'Sent' && disc.status !== 'Resolved';
              return isStale && notSent;
            });

            if (staleDiscrepancies.length === 0) return null;

            return (
              <div className="stale-discrepancies-section">
                <div className="discrepancies-header">
                  <AlertCircle size={16} className="warning-icon" />
                  <h4>Over 24 Hr Discrepancies ({staleDiscrepancies.length})</h4>
                </div>
                <div className="discrepancies-list">
                  {staleDiscrepancies.map((disc, index) => {
                    const createdAt = new Date(disc.created_at);
                    const daysSinceCreated = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
                    
                    return (
                      <div key={disc.id || index} className="discrepancy-item">
                        <div className="discrepancy-header">
                          <span className="discrepancy-type">{disc.type || 'Unknown Type'}</span>
                          <span className="discrepancy-age">
                            {daysSinceCreated === 0 ? 'Today' : 
                             daysSinceCreated === 1 ? '1 day ago' : 
                             `${daysSinceCreated} days ago`}
                          </span>
                        </div>
                        {disc.additional_information && (
                          <div className="discrepancy-details">
                            {disc.additional_information}
                          </div>
                        )}
                        <div className="discrepancy-meta">
                          <span className="discrepancy-status">Status: {disc.status || 'Pending'}</span>
                          {disc.created_by_name && (
                            <span className="discrepancy-creator">Created by: {disc.created_by_name}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Hold Information Split Layout - only show for projects on hold */}
          {formData.modelingState === 'Hold' && (
            <div className="hold-info-split-container">
              {/* Left Side - Hold Information */}
              <div className="hold-info-box-compact">
                <div className="hold-header-compact">
                  <AlertCircle size={16} />
                  <span>Hold Information</span>
                </div>
                <div className="hold-content-compact">
                  <div>
                    <span className="hold-label">Reason:</span>
                    <span className="hold-value">{(() => {
                      const reasonMap = {
                        waiting_discrepancy_clarification: "Waiting for Discrepancy Clarification",
                        client_requested_hold: "Client Requested Hold",
                        waiting_for_information: "Waiting for Information",
                        internal_review: "Internal Review",
                        waiting_updated_pdfs: "Waiting Updated PDFs",
                        waiting_updated_cad: "Waiting Updated CAD",
                        waiting_client_response: "Waiting Client Response",
                        other: "Other"
                      };
                      return reasonMap[formData.holdReason] || 'No reason provided';
                    })()}</span>
                  </div>

                  {formData.holdNotes && (
                    <div>
                      <span className="hold-label">Notes:</span>
                      <span className="hold-value">{formData.holdNotes}</span>
                    </div>
                  )}

                  <div>
                    <span className="hold-label">On Hold Since:</span>
                    <span className="hold-value">
                      {formData.holdStartDate ? (() => {
                        const date = new Date(formData.holdStartDate);
                        const start = new Date(formData.holdStartDate);
                        const now = new Date();
                        start.setHours(0, 0, 0, 0);
                        now.setHours(0, 0, 0, 0);
                        const diffDays = Math.floor((now - start) / (1000 * 60 * 60 * 24));
                        const daysOnHold = diffDays + 1;
                        return `${date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} (${daysOnHold} day${daysOnHold !== 1 ? 's' : ''})`;
                      })() : 'Not set'}
                    </span>
                  </div>

                  {(emailTracking?.initial_follow_up_days || emailTracking?.recurring_follow_up_days) && (
                    <div>
                      <span className="hold-label">Follow-up:</span>
                      <span className="hold-value">
                        {emailTracking?.initial_follow_up_days && `Initial: ${emailTracking.initial_follow_up_days} days`}
                        {emailTracking?.initial_follow_up_days && emailTracking?.recurring_follow_up_days && ' • '}
                        {emailTracking?.recurring_follow_up_days && `Recurring: ${emailTracking.recurring_follow_up_days} days`}
                      </span>
                    </div>
                  )}

                  {emailTracking?.last_email_sent_date && (
                    <div>
                      <span className="hold-label">Last Email:</span>
                      <span className="hold-value">
                        {new Date(emailTracking.last_email_sent_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  )}

                  {emailTracking?.next_reminder_date && (
                    <div>
                      <span className="hold-label">Next Reminder:</span>
                      <span className="hold-value">
                        {new Date(emailTracking.next_reminder_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - Additional Requests if they exist */}
              {formData.additionalRequests && formData.additionalRequests.length > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--dark-surface)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-lg)',
                }}>
                  <Package size={18} style={{ color: 'var(--gray-400)', marginTop: '2px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-400)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Additional Requests
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                      {formData.additionalRequests.map((request, index) => (
                        <span
                          key={index}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 10px',
                            backgroundColor: 'var(--gray-700)',
                            color: 'var(--gray-300)',
                            border: '1px solid var(--gray-600)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-medium)',
                          }}
                        >
                          {request}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quick Deadline Box - show when there are deadline issues */}
          {(() => {
            const deadlineItems = [];
            const now = new Date();
            
            // Check 1: If submitted (in Review status) and finish grade due in less than a week
            const finishGradeDueDate = project?.finishGradeDueDate || project?.finish_grade_due_date;
            if (formData.modelingStatus === 'Review' && finishGradeDueDate) {
              const finishGradeDue = new Date(finishGradeDueDate);
              const daysUntilDue = Math.floor((finishGradeDue - now) / (1000 * 60 * 60 * 24));
              
              if (daysUntilDue < 7 && daysUntilDue >= 0) {
                deadlineItems.push({
                  type: daysUntilDue <= 3 ? 'danger' : 'warning',
                  reason: 'Finish Grade Due Soon',
                  description: `Finish grade due in ${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''}`,
                  dates: {
                    'Finish Grade Due': finishGradeDue.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                    'Status': formData.modelingStatus || 'In Progress'
                  }
                });
              } else if (daysUntilDue < 0) {
                deadlineItems.push({
                  type: 'danger',
                  reason: 'Finish Grade Overdue',
                  description: `Finish grade overdue by ${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) !== 1 ? 's' : ''}`,
                  dates: {
                    'Finish Grade Due': finishGradeDue.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                    'Status': formData.modelingStatus || 'In Progress'
                  }
                });
              }
            }
            
            // Check 2: If projected completion is after due date
            const projectedCompletionDate = project?.finalProjectedCompletionDate || project?.final_projected_completion_date || 
                                            project?.projectedCompletionDate || project?.projected_completion_date;
            const dueDateValue = project?.dueDate || project?.due_date || 
                                 project?.finishGradeDueDate || project?.finish_grade_due_date;
            
            if (projectedCompletionDate && dueDateValue) {
              const projectedCompletion = new Date(projectedCompletionDate);
              const dueDate = new Date(dueDateValue);
              
              if (projectedCompletion > dueDate) {
                const daysLate = Math.floor((projectedCompletion - dueDate) / (1000 * 60 * 60 * 24));
                deadlineItems.push({
                  type: 'danger',
                  reason: 'Projected Completion After Due Date',
                  description: `Project will be ${daysLate} day${daysLate !== 1 ? 's' : ''} late based on projection`,
                  dates: {
                    'Due Date': dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                    'Projected': projectedCompletion.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  }
                });
              }
            }
            
            if (deadlineItems.length > 0) {
              return (
                <div className="deadline-box">
                  <div className="deadline-header">
                    <Clock size={20} />
                    <h3>Quick Deadline</h3>
                  </div>
                  <div className="deadline-content">
                    {deadlineItems.map((item, index) => (
                      <div key={index} className={`deadline-item deadline-${item.type}`}>
                        <div className="deadline-reason">{item.reason}</div>
                        <div className="deadline-description">{item.description}</div>
                        <div className="deadline-dates">
                          {Object.entries(item.dates).map(([label, value]) => (
                            <span key={label}>
                              <strong>{label}:</strong> {value}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            
            return null;
          })()}
            
          {/* Additional Info - show above email controls */}
          {(formData.additionalInfo || (formData.additionalRequests && formData.additionalRequests.length > 0 && formData.modelingState !== 'Hold')) && (
            <div className="modal-row-half">
              {formData.additionalInfo && (
                <div className="modal-field-half">
                  <label>Additional Info</label>
                  <div className="additional-info-display">
                    {formData.additionalInfo}
                  </div>
                </div>
              )}

              {/* Only show Additional Requests here if NOT on hold (hold projects show it in split container) */}
              {formData.additionalRequests && formData.additionalRequests.length > 0 && formData.modelingState !== 'Hold' && (
                <div className="modal-field-half">
                  <label>Additional Requests</label>
                  <div className="additional-requests-display">
                    <ul className="requests-list">
                      {formData.additionalRequests.map((request, index) => (
                        <li key={index}>{request}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Email Controls Section - For Hold projects AND Overdue projects */}
          {(formData.modelingState === 'Hold' || (project?.dueDate && new Date(project.dueDate) < new Date())) && (
              <div className="email-controls-section">
                <h3 className="section-title">Email Notifications</h3>
                <div className="email-controls">
                  <div style={{
                    display: 'flex',
                    gap: 'var(--space-8)',
                    flexWrap: 'wrap',
                    marginBottom: 'var(--space-4)'
                  }}>
                    <Toggle
                      label="Send One-Time Email"
                      checked={sendOneTimeEmail}
                      onChange={(e) => handleSendOneTimeEmailToggle(e.target.checked)}
                      labelPosition="right"
                    />

                    <Toggle
                      label="Auto Recurring Email"
                      checked={autoRecurringEmail}
                      onChange={(e) => handleAutoRecurringToggle(e.target.checked)}
                      labelPosition="right"
                    />

                    <Toggle
                      label="Edit Recurring Notification"
                      checked={sendEmail}
                      onChange={(e) => handleSendEmailToggle(e.target.checked)}
                      disabled={!autoRecurringEmail}
                      labelPosition="right"
                    />

                    <Toggle
                      label="Include Project Discrepancies"
                      checked={includeDiscrepancies}
                      onChange={(e) => {
                        setIncludeDiscrepancies(e.target.checked);
                        // Select all discrepancies when enabling, clear when disabling
                        if (e.target.checked && projectDiscrepancies.length > 0) {
                          setSelectedDiscrepancyIds(projectDiscrepancies.map(d => d.id));
                        } else {
                          setSelectedDiscrepancyIds([]);
                        }
                      }}
                      labelPosition="right"
                    />
                  </div>
                  
                  {includeDiscrepancies && projectDiscrepancies.length > 0 && (
                    <div className="discrepancies-preview">
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 'var(--space-3)'
                      }}>
                        <label style={{ fontWeight: 600, color: 'var(--gray-50)' }}>
                          Select Discrepancies ({selectedDiscrepancyIds.length} of {projectDiscrepancies.length}):
                        </label>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedDiscrepancyIds(projectDiscrepancies.map(d => d.id))}
                          >
                            Select All
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedDiscrepancyIds([])}
                          >
                            Deselect All
                          </Button>
                        </div>
                      </div>
                      <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-2)'
                      }}>
                        {projectDiscrepancies.map((disc) => (
                          <div
                            key={disc.id}
                            className="discrepancy-item"
                            style={{
                              cursor: 'pointer',
                              userSelect: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-3)',
                              padding: 'var(--space-2) var(--space-3)'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedDiscrepancyIds.includes(disc.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDiscrepancyIds([...selectedDiscrepancyIds, disc.id]);
                                } else {
                                  setSelectedDiscrepancyIds(selectedDiscrepancyIds.filter(id => id !== disc.id));
                                }
                              }}
                            />
                            <span className="disc-type">{disc.type}</span>
                            <span className="disc-status" data-status={disc.status}>{disc.status}</span>
                            <div className="disc-attachments">
                              {disc.markup_attachment && (
                                <span className="disc-attachment" title="Has file attachment">[File]</span>
                              )}
                              {disc.loom_link && (
                                <span className="disc-attachment" title="Has video">[Video]</span>
                              )}
                            </div>
                            <span className="disc-date">
                              {disc.sent_date ? 
                                new Date(disc.sent_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) :
                                'Not sent'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {includeDiscrepancies && projectDiscrepancies.length === 0 && !loadingDiscrepancies && (
                    <div className="discrepancies-preview empty">
                      <label>No discrepancies found for this project</label>
                    </div>
                  )}
                  
                  {includeDiscrepancies && loadingDiscrepancies && (
                    <div className="discrepancies-preview loading">
                      <label>Loading discrepancies...</label>
                    </div>
                  )}
                  
                  {(sendEmail || sendOneTimeEmail) && (
                    <div className="hold-email-content">
                      <div className="hold-email-row">
                        {generatingEmail && (
                          <div style={{
                            color: 'var(--gray-400)',
                            fontSize: 'var(--text-sm)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                          }}>
                            <Mail size={16} />
                            Generating email...
                          </div>
                        )}
                        
                        {(sendEmail || autoRecurringEmail) && (
                          <Input
                            type="number"
                            label="Recurring Follow-up (days)"
                            value={recurringFollowUpDays}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty string while typing, otherwise parse the number
                              if (value === '') {
                                setRecurringFollowUpDays('');
                              } else {
                                const numValue = parseInt(value);
                                if (!isNaN(numValue) && numValue >= 1 && numValue <= 30) {
                                  setRecurringFollowUpDays(numValue);
                                }
                              }
                            }}
                            onBlur={(e) => {
                              // On blur, if empty or invalid, set to default
                              if (e.target.value === '' || parseInt(e.target.value) < 1) {
                                setRecurringFollowUpDays(7);
                              }
                            }}
                            min={1}
                            max={30}
                            size="sm"
                            style={{ width: '200px' }}
                          />
                        )}
                        
                        {error && (
                          <div style={{
                            color: 'var(--danger)',
                            fontSize: 'var(--text-sm)',
                            marginLeft: 'var(--space-3)',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            ⚠️ {error}
                          </div>
                        )}
                      </div>
                      
                      {/* Email Type Toggle - Only show if both email types are enabled */}
                      {(sendOneTimeEmail || sendEmail) && (
                        <div className="email-type-toggle">
                          <div className="email-type-toggle-container">
                            <span className="email-type-toggle-label">Currently Editing:</span>
                            <div className="email-type-toggle-buttons">
                              <button
                                type="button"
                                className={`email-type-toggle-button ${editingEmailType === 'one-time' ? 'active' : ''}`}
                                onClick={() => {
                                  if (editingEmailType !== 'one-time') {
                                    handleEmailTypeToggle();
                                  }
                                }}
                                disabled={!sendOneTimeEmail}
                              >
                                One-Time Email
                              </button>
                              <button
                                type="button"
                                className={`email-type-toggle-button ${editingEmailType === 'recurring' ? 'active' : ''}`}
                                onClick={() => {
                                  if (editingEmailType !== 'recurring') {
                                    handleEmailTypeToggle();
                                  }
                                }}
                                disabled={!sendEmail}
                              >
                                Recurring Email
                              </button>
                            </div>
                            <span className="email-type-toggle-hint">
                              {editingEmailType === 'one-time' ? 
                                'Editing one-time quick deadline email' : 
                                'Editing recurring notification template'}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="hold-email-recipients">
                        <div className="recipient-field">
                          <label>To:</label>
                          <MultiSelectSearchable
                            options={allContactsWithClients.map(c => ({
                              id: c.id,
                              name: c.name,
                              email: c.email,
                              client: c.clientName
                            }))}
                            selected={emailTo}
                            onChange={setEmailTo}
                            placeholder="Select recipients..."
                            getOptionLabel={(option) => option.email ? `${option.name} (${option.email.toLowerCase()})` : option.name}
                            getOptionEmail={(option) => option.email}
                            searchFields={['name', 'email']}
                            getOptionClient={(option) => option.client}
                          />
                        </div>
                        
                        <div className="recipient-field">
                          <label>Cc:</label>
                          <MultiSelectSearchable
                            options={allContactsWithClients.map(c => ({
                              id: c.id,
                              name: c.name,
                              email: c.email,
                              client: c.clientName
                            }))}
                            selected={emailCc}
                            onChange={setEmailCc}
                            placeholder="Select CC recipients..."
                            getOptionLabel={(option) => option.email ? `${option.name} (${option.email.toLowerCase()})` : option.name}
                            getOptionEmail={(option) => option.email}
                            searchFields={['name', 'email']}
                            getOptionClient={(option) => option.client}
                          />
                        </div>
                      </div>


                      <Input
                        type="text"
                        label="Subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="Email subject will be generated..."
                        size="md"
                      />
                      
                      <div className="hold-email-split-container">
                        {/* Left Side - Plain Text Editor */}
                        <div className="hold-email-editor">
                          <div className="hold-email-editor-header">
                            <h5>Email Content</h5>
                            <small>Plain text editor</small>
                          </div>
                          <textarea
                            value={emailPreview}
                            onChange={(e) => setEmailPreview(e.target.value)}
                            className="hold-email-body"
                            rows="15"
                            placeholder="Email content will be generated when you click 'Generate Email'..."
                          />
                          <div className="email-size-indicator">
                            <span className={`character-count ${emailPreview?.length > 60000 ? 'warning' : emailPreview?.length > 50000 ? 'caution' : ''}`}>
                              {(emailPreview?.length || 0).toLocaleString()} / 64,000 characters
                              {emailPreview?.length > 60000 && ' ⚠️ Approaching limit'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Right Side - HTML Preview */}
                        <div className="hold-email-preview">
                          <div className="hold-email-preview-header">
                            <h5>Email Preview</h5>
                            <small>
                              {updatingPreview ? (
                                <span style={{ color: '#3b82f6' }}>
                                  Updating preview...
                                </span>
                              ) : (
                                'How the email will appear'
                              )}
                            </small>
                          </div>
                          <div className="hold-email-preview-content">
                            <iframe
                              className="hold-email-preview-iframe"
                              srcDoc={emailHtml || '<div style="padding: 20px; text-align: center; color: #666;">Email preview will appear here</div>'}
                              title="Email Preview"
                              style={{ opacity: updatingPreview ? 0.7 : 1, transition: 'opacity 0.15s' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
          )}
        </ModalBody>

        <ModalFooter style={{ background: 'var(--dark-surface)', borderTop: '1px solid var(--gray-700)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <Button
                variant="danger"
                size="md"
                icon={<Trash2 size={16} />}
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
              >
                Delete Project
              </Button>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
              <Button variant="ghost" size="md" onClick={onClose}>
                Close
              </Button>
            {(formData.modelingState === 'Hold' || (project?.dueDate && new Date(project.dueDate) < new Date())) && (
              <>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  loading={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                {sendEmail && emailTo.length > 0 && emailPreview && (
                  <Button
                    variant="primary"
                    size="md"
                    icon={<Mail size={16} />}
                    onClick={() => handleSave(false)}
                    disabled={saving || sendingEmail}
                    loading={saving}
                  >
                    {saving ? 'Saving...' : 'Save Recurring Template'}
                  </Button>
                )}
                {sendOneTimeEmail && emailTo.length > 0 && emailPreview && (
                  <Button
                    variant="success"
                    size="md"
                    icon={emailSent ? <Check size={16} /> : <Send size={16} />}
                    onClick={handleSendEmail}
                    disabled={saving || sendingEmail || !emailPreview || emailTo.length === 0}
                    loading={sendingEmail}
                  >
                    {emailSent ? 'Email Sent!' : sendingEmail ? 'Sending...' : 'Send Email'}
                  </Button>
                )}
              </>
            )}
            </div>
          </div>
        </ModalFooter>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${project?.projectName || 'Unnamed Project'}"? This action cannot be undone.`}
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
        zIndex={1100}
      />
    </Modal>
  );
};

export default ModelBuildingManagerModal;